import { useEffect, useState } from "react";
import { RefreshCw, LogOut, Search, Loader2, Lock, FileSpreadsheet, Trash2, Copy, Check, Mail, RotateCcw, Link2, UserMinus } from "lucide-react";
import * as XLSX from "xlsx";
import { copyText } from "@/utils/clipboard";
import { WaIcon } from "@/components/BrandIcons";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Admin = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem("prenet-admin-token") || "");
  const [passcode, setPasscode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [fetching, setFetching] = useState(false);
  const [stats, setStats] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmDetachId, setConfirmDetachId] = useState(null);
  const [detaching, setDetaching] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [waConfirm, setWaConfirm] = useState(false);
  const [waStatus, setWaStatus] = useState(null);

  const copyReferral = async (r) => {
    const ok = await copyText(`${window.location.origin}/r/${r.username}`);
    if (ok) {
      setCopiedId(r.id);
      setTimeout(() => setCopiedId((c) => (c === r.id ? null : c)), 1600);
    }
  };

  const notifyWhatsapp = async () => {
    if (!waConfirm) {
      setWaConfirm(true);
      return;
    }
    try {
      const res = await fetch(`${API}/admin/notify-whatsapp`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      setWaStatus(
        d.queued > 0
          ? `Newsletter lancée : ${d.queued} membre(s) notifié(s).`
          : "Tous les membres ont déjà été notifiés."
      );
    } catch {
      setWaStatus("Erreur — réessayez.");
    }
    setWaConfirm(false);
  };

  const [nlSubject, setNlSubject] = useState("");
  const [nlMessage, setNlMessage] = useState("");
  const [nlConfirm, setNlConfirm] = useState(false);
  const [nlStatus, setNlStatus] = useState(null);

  const sendNewsletter = async () => {
    if (!nlConfirm) {
      if ((nlMessage || "").trim().length < 5) {
        setNlStatus("Écrivez d'abord le message de la newsletter.");
        return;
      }
      setNlConfirm(true);
      return;
    }
    try {
      const res = await fetch(`${API}/admin/send-newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ subject: nlSubject, message: nlMessage }),
      });
      const d = await res.json();
      if (res.ok) {
        setNlStatus(`Newsletter envoyée à ${d.queued} membre(s).`);
        loadNewsletters(token);
      } else if (d.detail === "CONTENT_REJECTED") {
        setNlStatus("Contenu refusé par le filtre (lien non https ?). Modifiez le message.");
      } else {
        setNlStatus("Erreur — réessayez.");
      }
    } catch {
      setNlStatus("Erreur réseau — réessayez.");
    }
    setNlConfirm(false);
  };

  const [nlHistory, setNlHistory] = useState([]);

  const loadNewsletters = async (tok) => {
    try {
      const res = await fetch(`${API}/admin/newsletters`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (res.ok) setNlHistory((await res.json()).newsletters || []);
    } catch {}
  };

  const reuseNewsletter = (n) => {
    setNlSubject(n.subject || "");
    setNlMessage(n.message || "");
    setNlStatus(`Brouillon « ${n.subject || "Sans sujet"} » chargé — modifiez puis renvoyez.`);
  };

  const load = async (tok) => {
    setFetching(true);
    try {
      const res = await fetch(`${API}/admin/registrations`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (res.status === 401) {
        sessionStorage.removeItem("prenet-admin-token");
        setToken("");
        setError("Session expirée. Veuillez vous reconnecter.");
        return;
      }
      const json = await res.json();
      setData(json);
    } catch {
      setError("Impossible de joindre le serveur.");
    } finally {
      setFetching(false);
    }
  };

  const loadStats = async (tok) => {
    try {
      const res = await fetch(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (res.ok) setStats(await res.json());
    } catch {}
  };

  useEffect(() => {
    if (token) {
      load(token);
      loadStats(token);
      loadNewsletters(token);
    }
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ passcode }),
      });
      const json = await res.json();
      if (!res.ok) {
        const d = json.detail;
        setError(
          d === "LOCKED"
            ? "Trop de tentatives. Réessayez dans 15 minutes."
            : d === "WRONG_PASSCODE"
              ? "Code d'accès incorrect."
              : "Échec de la connexion."
        );
        return;
      }
      sessionStorage.setItem("prenet-admin-token", json.access_token);
      setToken(json.access_token);
      setPasscode("");
    } catch {
      setError("Impossible de joindre le serveur.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("prenet-admin-token");
    setToken("");
    setData(null);
  };

  const remove = async (id) => {
    setDeleting(true);
    try {
      const res = await fetch(`${API}/admin/registrations/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        sessionStorage.removeItem("prenet-admin-token");
        setToken("");
        setError("Session expirée. Veuillez vous reconnecter.");
        return;
      }
      if (res.ok) {
        setConfirmId(null);
        await load(token);
        loadStats(token);
      }
    } catch {}
    setDeleting(false);
  };

  const detachSponsor = async (id) => {
    setDetaching(true);
    try {
      const res = await fetch(`${API}/admin/registrations/${id}/detach-sponsor`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        sessionStorage.removeItem("prenet-admin-token");
        setToken("");
        setError("Session expirée. Veuillez vous reconnecter.");
        return;
      }
      if (res.ok) {
        setConfirmDetachId(null);
        await load(token);
        loadStats(token);
      }
    } catch {}
    setDetaching(false);
  };

  const countryNameFr = (code) => {
    if (!code) return "";
    try {
      return new Intl.DisplayNames(["fr"], { type: "region" }).of(code.toUpperCase()) || code;
    } catch {
      return code;
    }
  };

  const exportExcel = () => {
    if (!data) return;
    // Chronological order: first registered at the top, latest at the bottom.
    // Position = global rank (offset covers deleted records).
    const total = data.registrations.length;
    const offset = data.count - total;
    const head = ["Position", "Date", "Nom", "Email", "Pseudo", "Langue", "Pays", "Parrain", "Lien de parrainage"];
    const rows = [...data.registrations]
      .sort((a, b) => (a.id < b.id ? -1 : 1))
      .map((r, j) => [
        offset + j + 1,
        new Date(r.created_at),
        r.name,
        r.email,
        r.username,
        (r.lang || "").toUpperCase(),
        countryNameFr(r.country),
        r.sponsor || "",
        `${window.location.origin}/r/${r.username}`,
      ]);
    const ws = XLSX.utils.aoa_to_sheet([head, ...rows], { cellDates: true });
    rows.forEach((_, i) => {
      const addr = XLSX.utils.encode_cell({ r: i + 1, c: 1 });
      if (ws[addr]) ws[addr].z = "dd/mm/yyyy hh:mm";
    });
    ws["!cols"] = [
      { wch: 10 },
      { wch: 18 },
      { wch: 26 },
      { wch: 34 },
      { wch: 20 },
      { wch: 8 },
      { wch: 20 },
      { wch: 46 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Inscrits");
    XLSX.writeFile(wb, "prenet-inscrits.xlsx", { cellDates: true });
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-night px-5">
        <Seo noindex />
        <form
          onSubmit={login}
          className="glass w-full max-w-sm rounded-3xl p-9"
          data-testid="admin-login-form"
        >
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="PreNet" className="h-12 w-auto" data-testid="admin-logo-image" />
            <div>
              <h1 className="font-display text-lg font-bold text-ink">PreNet Admin</h1>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-subtle">
                Inscriptions
              </p>
            </div>
          </div>
          <div className="mt-8">
            <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-ink-muted">
              Code d'accès
            </label>
            <input
              type="password"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              data-testid="admin-passcode-input"
              className="w-full rounded-xl border border-white/10 bg-night-2/80 px-4 py-3.5 text-sm text-ink outline-none transition-all focus:border-signal/60 focus:ring-1 focus:ring-signal/40"
              autoFocus
            />
          </div>
          {error && (
            <p className="mt-3 text-xs text-tactical" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-button"
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-signal py-3.5 text-sm font-bold text-night transition-all hover:glow-signal disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={15} />}
            Se connecter
          </button>
        </form>
      </div>
    );
  }

  const rows = (data?.registrations || []).filter((r) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      r.name.toLowerCase().includes(q) ||
      r.email.toLowerCase().includes(q) ||
      r.username.toLowerCase().includes(q) ||
      (r.sponsor || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen bg-night px-5 py-10 sm:px-8" data-testid="admin-view">
      <Seo noindex />
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="PreNet" className="h-10 w-auto" data-testid="admin-logo-image" />
            <div>
              <h1 className="font-display text-lg font-bold text-ink">PreNet Admin</h1>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-subtle" data-testid="admin-count">
                {data ? `${data.count} inscriptions` : "Loading…"}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-3">
            <div className="relative">
              <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-subtle" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher…"
                data-testid="admin-search-input"
                className="w-48 rounded-full border border-white/10 bg-night-2/80 py-2.5 pl-9 pr-4 text-xs text-ink outline-none focus:border-signal/60"
              />
            </div>
            <button
              type="button"
              onClick={() => {
                load(token);
                loadStats(token);
              }}
              data-testid="admin-refresh-button"
              className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2.5 text-xs font-semibold text-ink-muted transition-colors hover:border-signal/50 hover:text-signal"
            >
              <RefreshCw size={13} className={fetching ? "animate-spin" : ""} />
              Rafraîchir
            </button>
            <button
              type="button"
              onClick={exportExcel}
              data-testid="admin-export-button"
              className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2.5 text-xs font-semibold text-ink-muted transition-colors hover:border-signal/50 hover:text-signal"
            >
              <FileSpreadsheet size={13} />
              Excel
            </button>
            <button
              type="button"
              onClick={notifyWhatsapp}
              data-testid={waConfirm ? "admin-whatsapp-confirm" : "admin-whatsapp-button"}
              className={
                waConfirm
                  ? "flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-xs font-bold text-night"
                  : "flex items-center gap-2 rounded-full border border-[#25D366]/50 px-5 py-2.5 text-xs font-bold text-[#25D366] transition-colors hover:bg-[#25D366]/10"
              }
            >
              <WaIcon size={13} />
              {waConfirm ? "Confirmer l'envoi ?" : "Canal WhatsApp"}
            </button>
            <button
              type="button"
              onClick={logout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2.5 text-xs font-semibold text-ink-muted transition-colors hover:border-tactical/60 hover:text-tactical"
            >
              <LogOut size={13} />
              Quitter
            </button>
          </div>
        </div>
        {waStatus && (
          <p className="mt-3 text-right font-mono text-[11px] text-signal2" data-testid="admin-whatsapp-status">
            {waStatus}
          </p>
        )}

        <div className="mt-8 overflow-x-auto rounded-2xl border border-white/8" data-testid="admin-registrations-table">
          <table className="w-full text-left text-sm">
            <thead className="bg-night-2/90 font-mono text-[10px] uppercase tracking-[0.18em] text-ink-subtle">
              <tr>
                <th className="px-3 py-4 sm:px-5">#</th>
                <th className="hidden px-3 py-4 sm:px-5 md:table-cell">Date</th>
                <th className="px-3 py-4 sm:px-5">Nom</th>
                <th className="px-3 py-4 sm:px-5">Pseudo & lien</th>
                <th className="hidden px-3 py-4 sm:px-5 lg:table-cell">Email</th>
                <th className="px-3 py-4 sm:px-5">Langue</th>
                <th className="px-3 py-4 sm:px-5">Pays</th>
                <th className="px-3 py-4 sm:px-5">Parrain</th>
                <th className="px-3 py-4 sm:px-5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id} className="border-t border-white/5 transition-colors hover:bg-white/[0.03]">
                  <td className="px-3 py-4 font-mono text-xs text-signal sm:px-5">{data.count - i}</td>
                  <td className="hidden px-3 py-4 font-mono text-xs text-ink-subtle sm:px-5 md:table-cell">
                    {new Date(r.created_at).toLocaleString()}
                  </td>
                  <td className="px-3 py-4 font-medium text-ink sm:px-5">{r.name}</td>
                  <td className="px-3 py-4 sm:px-5">
                    <span className="block font-mono text-xs text-signal">@{r.username}</span>
                    <button
                      type="button"
                      onClick={() => copyReferral(r)}
                      data-testid={`admin-referral-link-${r.id}`}
                      title={`Copier : ${window.location.origin}/r/${r.username}`}
                      className="mt-1 flex items-center gap-1.5 text-left font-mono text-[10px] text-ink-subtle transition-colors hover:text-signal"
                    >
                      {copiedId === r.id ? (
                        <Check size={10} className="shrink-0 text-signal2" />
                      ) : (
                        <Link2 size={10} className="shrink-0" />
                      )}
                      <span>{`/r/${r.username}`}</span>
                    </button>
                  </td>
                  <td className="hidden px-3 py-4 text-ink-muted sm:px-5 lg:table-cell">{r.email}</td>
                  <td className="px-3 py-4 font-mono text-xs uppercase text-ink-muted sm:px-5">{r.lang || "—"}</td>
                  <td className="px-3 py-4 text-ink-muted sm:px-5">{r.country ? countryNameFr(r.country) : "—"}</td>
                  <td className="px-3 py-4 font-mono text-xs text-ink-muted sm:px-5">
                    {r.sponsor ? `@${r.sponsor}` : "—"}
                  </td>
                  <td className="px-3 py-4 sm:px-5">
                    {confirmId === r.id ? (
                      <span className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => remove(r.id)}
                          disabled={deleting}
                          data-testid={`admin-confirm-delete-${r.id}`}
                          className="rounded-full bg-tactical px-3 py-1.5 text-[10px] font-bold text-night disabled:opacity-60"
                        >
                          {deleting ? "…" : "Confirmer"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmId(null)}
                          data-testid={`admin-cancel-delete-${r.id}`}
                          className="rounded-full border border-white/10 px-3 py-1.5 text-[10px] font-semibold text-ink-muted transition-colors hover:text-ink"
                        >
                          Annuler
                        </button>
                      </span>
                    ) : confirmDetachId === r.id ? (
                      <span className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => detachSponsor(r.id)}
                          disabled={detaching}
                          data-testid={`admin-confirm-detach-${r.id}`}
                          className="rounded-full bg-signal px-3 py-1.5 text-[10px] font-bold text-night disabled:opacity-60"
                        >
                          {detaching ? "…" : "Retirer"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDetachId(null)}
                          data-testid={`admin-cancel-detach-${r.id}`}
                          className="rounded-full border border-white/10 px-3 py-1.5 text-[10px] font-semibold text-ink-muted transition-colors hover:text-ink"
                        >
                          Annuler
                        </button>
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => copyReferral(r)}
                          data-testid={`admin-copy-link-${r.id}`}
                          aria-label="Copier le lien de parrainage"
                          title="Copier le lien de parrainage"
                          className="flex h-7 w-7 items-center justify-center rounded-md border border-white/10 text-ink-subtle transition-colors hover:border-signal/50 hover:text-signal"
                        >
                          {copiedId === r.id ? <Check size={13} className="text-signal2" /> : <Copy size={13} />}
                        </button>
                        {r.sponsor && (
                          <button
                            type="button"
                            onClick={() => setConfirmDetachId(r.id)}
                            data-testid={`admin-detach-button-${r.id}`}
                            aria-label="Retirer de la généalogie"
                            title="Retirer de la généalogie (détacher du parrain)"
                            className="flex h-7 w-7 items-center justify-center rounded-md border border-white/10 text-violet-400/70 transition-colors hover:border-violet-400/60 hover:text-violet-300"
                          >
                            <UserMinus size={13} />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setConfirmId(r.id)}
                          data-testid={`admin-delete-button-${r.id}`}
                          aria-label="Supprimer"
                          className="text-ink-subtle transition-colors hover:text-tactical"
                        >
                          <Trash2 size={15} />
                        </button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {!rows.length && (
                <tr>
                  <td colSpan={9} className="px-5 py-14 text-center text-sm text-ink-subtle" data-testid="admin-empty-state">
                    {data ? "Aucune inscription ne correspond." : "Chargement…"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-10 rounded-2xl border border-white/8 bg-night-2/70 p-7" data-testid="admin-newsletter-card">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-subtle">
            Newsletter
          </p>
          <div className="mt-5 grid gap-5 lg:grid-cols-2">
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.15em] text-ink-subtle">
                Sujet
              </label>
              <input
                type="text"
                value={nlSubject}
                onChange={(e) => setNlSubject(e.target.value)}
                placeholder="PreNet — actualités du pré-lancement"
                data-testid="admin-newsletter-subject"
                className="mt-2 w-full rounded-xl border border-white/10 bg-night px-4 py-3 text-sm text-ink outline-none transition-colors placeholder:text-ink-subtle focus:border-signal/60"
              />
              <label className="mt-4 block font-mono text-[10px] uppercase tracking-[0.15em] text-ink-subtle">
                Message
              </label>
              <textarea
                value={nlMessage}
                onChange={(e) => setNlMessage(e.target.value)}
                rows={7}
                placeholder="Collez ici le texte de votre newsletter…"
                data-testid="admin-newsletter-textarea"
                className="mt-2 w-full resize-y rounded-xl border border-white/10 bg-night px-4 py-3 text-sm leading-6 text-ink outline-none transition-colors placeholder:text-ink-subtle focus:border-signal/60"
              />
            </div>
            <div className="flex flex-col justify-between gap-4">
              <div className="rounded-xl border border-white/8 bg-night/60 p-4 text-xs leading-5 text-ink-muted">
                <p>
                  Le message part en l'état à <span className="font-bold text-ink">tous les inscrits</span> ({data ? data.count : "—"}), avec le logo et le pied d'email PreNet. Les sauts de ligne sont respectés et les liens https deviennent cliquables.
                </p>
                <p className="mt-2 font-mono text-[11px] text-ink-subtle">
                  Destinataires : {data ? data.count : "—"}
                </p>
              </div>
              <div>
                <button
                  type="button"
                  onClick={sendNewsletter}
                  data-testid={nlConfirm ? "admin-newsletter-confirm" : "admin-newsletter-send"}
                  className={
                    nlConfirm
                      ? "flex items-center gap-2 rounded-full bg-signal px-6 py-3 text-xs font-bold text-night"
                      : "flex items-center gap-2 rounded-full border border-signal/50 px-6 py-3 text-xs font-bold text-signal transition-colors hover:bg-signal/10"
                  }
                >
                  <Mail size={13} />
                  {nlConfirm ? "Confirmer l'envoi à tous ?" : "Envoyer la newsletter"}
                </button>
                {nlStatus && (
                  <p className="mt-3 font-mono text-[11px] text-signal2" data-testid="admin-newsletter-status">
                    {nlStatus}
                  </p>
                )}
                {nlHistory.length > 0 && (
                  <div className="mt-4 border-t border-white/8 pt-4">
                    <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-ink-subtle">
                      Envois précédents ({nlHistory.length}) — cliquez pour réutiliser
                    </p>
                    <div className="mt-3 max-h-44 space-y-2 overflow-y-auto pr-1">
                      {nlHistory.map((n) => (
                        <button
                          key={n.id}
                          type="button"
                          onClick={() => reuseNewsletter(n)}
                          data-testid={`admin-newsletter-reuse-${n.id}`}
                          className="group flex w-full items-center justify-between gap-3 rounded-xl border border-white/8 bg-night/60 px-3.5 py-2.5 text-left transition-colors hover:border-signal/40"
                        >
                          <span className="min-w-0">
                            <span className="block truncate text-xs font-medium text-ink">
                              {n.subject || "Sans sujet"}
                            </span>
                            <span className="block font-mono text-[10px] text-ink-subtle">
                              {(n.sent_at || "").slice(0, 16).replace("T", " ")} · {n.recipients} destinataire(s)
                            </span>
                          </span>
                          <RotateCcw size={13} className="shrink-0 text-ink-subtle transition-colors group-hover:text-signal" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 grid gap-5 lg:grid-cols-2" data-testid="admin-stats-panel">
          <div className="rounded-2xl border border-white/8 bg-night-2/70 p-7">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-subtle">
              Inscriptions par jour
            </p>
            {stats && stats.perDay && stats.perDay.length ? (
              <div className="mt-6 flex h-36 items-end gap-2" data-testid="admin-perday-chart">
                {stats.perDay.slice(-14).map((d, i, arr) => {
                  const max = Math.max(...arr.map((x) => x.count));
                  return (
                    <div key={d.date} className="flex flex-1 flex-col items-center gap-2">
                      <div className="flex w-full flex-1 items-end">
                        <div
                          data-testid={`admin-day-bar-${i}`}
                          className="w-full rounded-t bg-signal/70 transition-all"
                          style={{ height: `${Math.max(6, (d.count / max) * 100)}%` }}
                          title={`${d.date}: ${d.count}`}
                        />
                      </div>
                      <span className="font-mono text-[9px] text-ink-subtle">{d.date.slice(5)}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="mt-6 text-sm text-ink-subtle">Pas encore de données.</p>
            )}
          </div>
          <div className="rounded-2xl border border-white/8 bg-night-2/70 p-7">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-subtle">
              Meilleurs parrains
            </p>
            {stats && stats.topSponsors && stats.topSponsors.length ? (
              <div className="mt-6 space-y-4" data-testid="admin-sponsors-chart">
                {stats.topSponsors.map((sp, i) => {
                  const max = stats.topSponsors[0].count;
                  return (
                    <div key={sp.sponsor} className="flex items-center gap-3">
                      <span className="w-28 truncate font-mono text-xs text-signal">@{sp.sponsor}</span>
                      <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-white/5">
                        <div
                          data-testid={`admin-sponsor-bar-${i}`}
                          className="h-full rounded-full bg-tactical/80"
                          style={{ width: `${Math.max(8, (sp.count / max) * 100)}%` }}
                        />
                      </div>
                      <span className="w-6 text-right font-mono text-xs text-ink-muted">{sp.count}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="mt-6 text-sm text-ink-subtle">
                Pas encore de parrains — les premières invitations apparaîtront ici.
              </p>
            )}
          </div>
          <div className="rounded-2xl border border-white/8 bg-night-2/70 p-7">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-subtle">
              Pays les plus actifs
            </p>
            {stats && stats.topCountries && stats.topCountries.length ? (
              <div className="mt-6 space-y-4" data-testid="admin-countries-chart">
                {stats.topCountries.map((c, i) => {
                  const max = stats.topCountries[0].count;
                  return (
                    <div key={c.country} className="flex items-center gap-3">
                      <span className="w-28 truncate font-mono text-xs text-signal2">
                        {countryNameFr(c.country)}
                      </span>
                      <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-white/5">
                        <div
                          data-testid={`admin-country-bar-${i}`}
                          className="h-full rounded-full bg-signal2/80"
                          style={{ width: `${Math.max(8, (c.count / max) * 100)}%` }}
                        />
                      </div>
                      <span className="w-6 text-right font-mono text-xs text-ink-muted">{c.count}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="mt-6 text-sm text-ink-subtle">
                Pas encore de pays — les premières inscriptions apparaîtront ici.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;
